/**
 * Helpers for carrying the native signup handoff token across the social signup
 * flow.
 *
 * Background: for certain native applications (e.g. Dia) a `nativeHandoff` token
 * in the `/signup` URL allows the user to skip reCAPTCHA. When the user signs up
 * via a social provider they are redirected away to that provider and back onto
 * the social confirmation page, losing the token from the URL in the process.
 *
 * To preserve it, we persist the token to `sessionStorage` right before the
 * social redirect, then (on any IDAC page-state construction) read it back,
 * re-apply it to the URL, and immediately remove it from storage so it can be
 * consumed exactly like the password signup flow.
 *
 * All access is wrapped in try/catch because `sessionStorage` may be unavailable
 * in sandboxed native webviews or private browsing contexts. In those cases we
 * degrade gracefully (the user simply goes through the normal reCAPTCHA flow).
 */

/** URL query parameter carrying the native signup handoff token. */
export const NATIVE_HANDOFF_PARAM = 'nativeHandoff';

/** `sessionStorage` key under which the token is temporarily stashed. */
export const NATIVE_HANDOFF_STORAGE_KEY = 'nativeHandoffToken';

/**
 * Persist the native handoff token to `sessionStorage` if one is present in the
 * given location's query string. No-op when the param is absent (the common
 * case) or when storage is unavailable.
 */
export const maybeSaveNativeHandoffTokenFromUrl = (location: Location | URL): void => {
  try {
    const token = new URLSearchParams(location.search).get(NATIVE_HANDOFF_PARAM);
    if (token) {
      window.sessionStorage.setItem(NATIVE_HANDOFF_STORAGE_KEY, token);
    }
  } catch {
    // sessionStorage unavailable (e.g. sandboxed webview / private mode) - no-op.
  }
};

/**
 * Read the native handoff token from `sessionStorage` and remove it. Returns
 * `null` when no token is stored or when storage is unavailable.
 */
export const readAndClearNativeHandoffToken = (): string | null => {
  try {
    const token = window.sessionStorage.getItem(NATIVE_HANDOFF_STORAGE_KEY);
    if (token) {
      window.sessionStorage.removeItem(NATIVE_HANDOFF_STORAGE_KEY);
    }
    return token;
  } catch {
    // sessionStorage unavailable (e.g. sandboxed webview / private mode).
    return null;
  }
};

export const getUrlWithStoredNativeHandoffToken = (): URL | null => {
  const token = readAndClearNativeHandoffToken();
  if (!token) {
    return null;
  }

  const url = new URL(window.location.href);
  if (url.searchParams.get(NATIVE_HANDOFF_PARAM) === token) {
    return null;
  }

  url.searchParams.set(NATIVE_HANDOFF_PARAM, token);
  return url;
};
